<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoicesFilters',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'submit']);

  const props = defineProps({
    kind: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref({
    date_from: null,
    date_to: null,
    unpaid: false,
    kind: props.kind,
    date_type: 'invoice_date',
  });
  const ux = ref([]);
  const b_2248226175642056_options = computed(() => [
    {
      value: 'invoice_date',
      text: $fn.value.pgettext('Invoice', 'Invoice date'),
    },
    {
      value: 'fulfillment_date',
      text: $fn.value.pgettext('Invoice', 'Fulfillment date'),
    },
    {
      value: 'due_date',
      text: $fn.value.pgettext('Invoice', 'Due date'),
    },
  ]);

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_2248226175642056_options: b_2248226175642056_options.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item', cache: { type: 'local', key: 'InvoicesFilters' } },
      { name: 'ux' },
      { name: 'b_2248226175642056_options', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { item, ux, b_2248226175642056_options },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.kind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('kind');
    }
  );

  const act_5962862420439144_setData_ix4t6k7lw_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5962862420439144_setData_un9xibpjz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1048678296148443_emit_wogucndsm_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: { ...item.value, kind: props.kind },
    };
  };

  const act_5962862420439144_setData_ix4t6k7lw = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_ix4t6k7lw_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_3rrxmhn07 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_ix4t6k7lw,
      event: 'changed',
      displayName: 'setData',
    });
  const act_5962862420439144_setData_un9xibpjz = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_un9xibpjz_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_a30gsqflx =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_un9xibpjz,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_5962862420439144_changed_3rrxmhn07,
      inst_evh_5962862420439144_changed_a30gsqflx,
    ],
    event: 'changed',
  });
  const act_1048678296148443_emit_wogucndsm = new actionTypes.EmitAction({
    actionArgs: act_1048678296148443_emit_wogucndsm_getActionArgs,
  });
  const inst_evh_1048678296148443_clickPreventStop_figx4a7hk =
    new actionTypes.EventHandlerImpl({
      action: act_1048678296148443_emit_wogucndsm,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const inst_evh_1048678296148443_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1048678296148443_clickPreventStop_figx4a7hk],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_5962862420439144_changed_3rrxmhn07,
    inst_evh_5962862420439144_changed_a30gsqflx,
    inst_evh_5962862420439144_changed,
    inst_evh_1048678296148443_clickPreventStop_figx4a7hk,
    inst_evh_1048678296148443_clickPreventStop,
  };

  const evh_5962862420439144_changed_3rrxmhn07 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_3rrxmhn07.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed_a30gsqflx = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_a30gsqflx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPreventStop_figx4a7hk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1048678296148443_clickPreventStop_figx4a7hk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" :gap="2" v-if="loaded">
    <ItpFormSelect2
      name="date_type"
      v-model="item.date_type"
      :value="item.date_type"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (item.date_type =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
      :placeholder="pgettext('Invoices', 'date filter type')"
      :options="b_2248226175642056_options"
      class="w-auto"
    >
    </ItpFormSelect2>
    <DateRangeSelector
      name="invoiceFilters"
      :from="item.date_from"
      :to="item.date_to"
      @changed="evh_5962862420439144_changed($event, {})"
    >
      <ItpText :text="pgettext('Invoices', 'Unpaid')"> </ItpText>
    </DateRangeSelector>
    <ItpFormCheckbox
      name="unpaid"
      inline
      v-model="item.unpaid"
      :value="item.unpaid"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (item.unpaid =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
      <ItpText :text="pgettext('Invoices', 'Unpaid')"> </ItpText>
    </ItpFormCheckbox>
    <ItpButton
      :icon="$config.reloadIcon"
      :text="gettext('Load')"
      variant="light"
      @click.prevent.stop="evh_1048678296148443_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
</template>
